/**
 * HTML Parser — Parses form HTML into BuilderFieldDef array
 *
 * Reads HTML containing prm-* elements and extracts field definitions,
 * steps, sections, rules, and mappings into structured data for the
 * No-Code canvas.
 */

import {
    BuilderFieldDef,
    BuilderFieldRules,
    BuilderChoiceItem,
    BuilderStep,
    BuilderSection,
    FormBuilderState,
} from "../types/formBuilder.types";
import { resolveFieldType } from "../components/shared/FieldTypeRegistry";
import { newTranslationId } from "./translationIdentity";

const RULE_ATTRIBUTES = ["visible", "required", "readonly", "hidden", "disabled", "label"] as const;

/**
 * Parse an HTML string containing prm-* components into a FormBuilderState-compatible object.
 */
export const parseFormHtml = (html: string): Partial<FormBuilderState> => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(wrapInBody(normalizePrmSelfClosingTags(html)), "text/html");

    const formEl = doc.querySelector("prm-form");
    if (!formEl) {
        return {
            targetTable: "",
            onSubmit: "create",
            successMessage: "",
            successRedirect: "",
            recordId: "",
            eventsBinding: "",
            fields: [],
            steps: [],
            sections: [],
            htmlSource: html,
        };
    }

    // ── Extract form-level attributes ──
    const targetTable = formEl.getAttribute("prm:target-table") || "";
    const translationId = formEl.getAttribute("prm:translation-id") || "";
    const onSubmit = (formEl.getAttribute("prm:on-submit") || "create") as "create" | "update" | "create_or_update";
    const successMessage = formEl.getAttribute("prm:success-message") || "";
    const successRedirect = formEl.getAttribute("prm:success-redirect") || "";
    const recordId = formEl.getAttribute("prm:record-id") || "";
    const eventsBinding = formEl.getAttribute("prm:events") || "";

    // ── Extract steps ──
    const stepEls = formEl.querySelectorAll("prm-step");
    const steps: BuilderStep[] = [];
    const isMultiStep = stepEls.length > 0;

    stepEls.forEach((stepEl, idx) => {
        steps.push({
            title: stepEl.getAttribute("title") || `Step ${idx + 1}`,
            icon: stepEl.getAttribute("icon") || "",
            fields: [],
        });
    });

    // ── Extract sections ──
    const sectionEls = formEl.querySelectorAll("prm-section");
    const sections: BuilderSection[] = [];

    sectionEls.forEach((sectionEl) => {
        sections.push({
            title: sectionEl.getAttribute("title") || "",
            collapsible: sectionEl.getAttribute("collapsible") === "true",
            fields: [],
        });
    });

    // ── Extract fields ──
    const fields: BuilderFieldDef[] = [];
    const allFieldEls = formEl.querySelectorAll("[name]");
    const seenNames = new Set<string>();

    allFieldEls.forEach((el) => {
        const name = el.getAttribute("name");
        if (!name || seenNames.has(name)) return;
        seenNames.add(name);

        const tag = el.tagName.toLowerCase();
        const fieldType = resolveFieldType(tag);
        if (!fieldType) return;

        // Determine step index
        let stepIndex: number | null = null;
        if (isMultiStep) {
            const parentStep = el.closest("prm-step");
            if (parentStep) {
                stepIndex = Array.from(stepEls).indexOf(parentStep);
            }
        }

        // Determine section
        let sectionTitle: string | null = null;
        const parentSection = el.closest("prm-section");
        if (parentSection) {
            sectionTitle = parentSection.getAttribute("title") || null;
        }

        // Parse rules
        const rules = parseFieldRules(el);
        const ruleWarnings: string[] = [];
        let hasRuleWarnings = false;

        RULE_ATTRIBUTES.forEach((ruleType) => {
            const ruleValue = rules[ruleType];
            if (ruleValue && !isValidRuleExpression(ruleValue)) {
                hasRuleWarnings = true;
                ruleWarnings.push(`prm:rule:${ruleType} — invalid expression: "${ruleValue}"`);
            }
        });

        const field: BuilderFieldDef = {
            uid: el.getAttribute("prm:uid") || newTranslationId(),
            name,
            type: fieldType,
            label: el.getAttribute("label") || name,
            placeholder: el.getAttribute("placeholder") || "",
            required: el.getAttribute("required") === "true",
            map: el.getAttribute("prm:map") || "",
            source: el.getAttribute("prm:source") || "",
            display: el.getAttribute("prm:display") || "",
            valueField: el.getAttribute("prm:value-field") || "",
            referenceFilter: parseReferenceFilter(el.getAttribute("prm:reference-filter")),
            defaultValue: el.getAttribute("prm:default") || "",
            minLength: el.getAttribute("minlength") || "",
            maxLength: el.getAttribute("maxlength") || "",
            passwordPattern: el.getAttribute("pattern") || "",
            currencyCode: el.getAttribute("prm:currency-code") || "",
            currencyField: el.getAttribute("prm:currency-field") || "",
            accept: el.getAttribute("accept") || "",
            variant: el.getAttribute("prm:variant") || (fieldType === "boolean" ? "switch" : fieldType === "notice" ? "info" : ""),
            multiple: el.hasAttribute("multiple"),
            minDate: el.getAttribute("min") || "",
            maxDate: el.getAttribute("max") || "",
            options: el.getAttribute("options") || "",
            choiceItems: parseChoiceItems(el),
            className: el.getAttribute("class") || "",
            style: el.getAttribute("style") || "",
            rules,
            stepIndex,
            sectionTitle,
            hasRuleWarnings,
            ruleWarnings,
        };

        fields.push(field);

        // Add to step/section field lists
        if (stepIndex !== null && steps[stepIndex]) {
            steps[stepIndex].fields.push(name);
        }
        if (sectionTitle) {
            const section = sections.find((s) => s.title === sectionTitle);
            if (section) {
                section.fields.push(name);
            }
        }
    });

    return {
        translationId,
        targetTable,
        onSubmit,
        successMessage,
        successRedirect,
        recordId,
        eventsBinding,
        fields,
        steps,
        sections,
        htmlSource: html,
    };
};

const parseLegacyChoiceOptions = (value: string | null): BuilderChoiceItem[] => {
    if (!value) return [];

    return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => {
            const parts = item.split(":");
            if (parts.length === 1) return { value: parts[0].trim(), label: parts[0].trim() };

            return {
                value: parts[0].trim(),
                label: parts.slice(1).join(":").trim(),
            };
        });
};

const parseChoiceItems = (el: Element): BuilderChoiceItem[] => {
    const itemEls = Array.from(el.children).filter((child) => child.tagName.toLowerCase() === "prm-choice-field-item");
    if (!itemEls.length) return parseLegacyChoiceOptions(el.getAttribute("options"));

    return itemEls.map((itemEl) => ({
        label: itemEl.getAttribute("label") || itemEl.getAttribute("value") || "",
        value: itemEl.getAttribute("value") || itemEl.getAttribute("label") || "",
    }));
};

const parseReferenceFilter = (value: string | null): any => {
    if (!value) return null;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

/** Parse rule attributes from a field element */
const parseFieldRules = (el: Element): BuilderFieldRules => {
    const rules: BuilderFieldRules = {
        visible: "",
        required: "",
        readonly: "",
        hidden: "",
        disabled: "",
        label: "",
    };

    RULE_ATTRIBUTES.forEach((ruleType) => {
        const value = el.getAttribute(`prm:rule:${ruleType}`);
        if (value) {
            rules[ruleType] = value;
        }
    });

    return rules;
};

/**
 * Basic validation of a rule expression string.
 * Returns true if the expression appears well-formed.
 */
const isValidRuleExpression = (expr: string): boolean => {
    const trimmed = expr.trim();
    if (!trimmed) return true;
    if (trimmed === "true" || trimmed === "false") return true;

    // Check for balanced parentheses
    let depth = 0;
    for (const ch of trimmed) {
        if (ch === "(") depth++;
        if (ch === ")") depth--;
        if (depth < 0) return false;
    }
    if (depth !== 0) return false;

    // Check for at least one recognizable pattern
    const hasOperator = /==|!=|>=|<=|>|<|contains|empty|not_empty/.test(trimmed);
    const hasField = /[a-zA-Z_][a-zA-Z0-9_]*/.test(trimmed);

    return hasOperator || hasField;
};

const normalizePrmSelfClosingTags = (html: string): string =>
    String(html || "").replace(/<(prm-[a-z0-9-]+)([^>]*)\/>/gi, "<$1$2></$1>");

/** Wrap HTML in body tags for DOMParser */
const wrapInBody = (html: string): string => {
    if (html.includes("<body")) return html;
    return `<!DOCTYPE html><html><body>${html}</body></html>`;
};
